import { IF, UIRectangle, UIView } from "uicore-ts"
import { CBDataView } from "../CBDataView"
import { BSColor } from "./BSColor"
import { RHTableHeaderView } from "./RHTableHeaderView"
import { RHTableRowView } from "./RHTableRowView"
import { RHTextField } from "./RHTextField"

/**
 * RHDataView
 * Styles: Bootstrap .card
 * Uses RHTextField for the search bar.
 */
export class RHDataView<DataType = Record<string, any>> extends CBDataView<DataType> {
    
    // Override the property type to use the specialized component
    override searchTextField: RHTextField
    override tableHeaderView: RHTableHeaderView<DataType>
    
    constructor(elementID?: string) {
        
        super(elementID);
        
        // 1. Container Configuration (Bootstrap Card)
        (this as RHDataView<DataType>).configureWithObject({
            backgroundColor: BSColor.textWhite,
            style: {
                // display: "flex",
                // flexDirection: "column",
                // minWidth: "0",
                // wordWrap: "break-word",
                // backgroundClip: "border-box",
                border: `1px solid ${BSColor.borderLight.stringValue}`,
                borderRadius: "0.375rem",
                boxShadow: "none"
            }
        })
        
        // 2. Title Configuration
        this.titleLabel.configureWithObject({
            textColor: BSColor.dark,
            style: {
                fontSize: "1.25rem",
                fontWeight: "500",
                marginBottom: "0.5rem",
                lineHeight: "1.2"
            }
        })
        
        // 3. Search Bar Configuration
        // We replace the generic UITextField with RHTextField
        // @ts-ignore
        this.searchTextField.removeFromSuperview()
        
        // Instantiate the specialized RHTextField
        // It already handles borders, padding, focus rings, and validation internally.
        this.searchTextField = new RHTextField(this.elementID + "SearchTextField")
        this.addSubview(this.searchTextField)
        
        this.searchTextField.configureWithObject({
            placeholderText: "Search...",
            // We only need to set layout-specific properties here.
            // Colors, borders, and interaction states are handled by RHTextField.
            style: {
                width: "100%",
                marginTop: "0px"
            }
        })
        
        // Re-attach the listener for the DataView's filtering logic
        this.searchTextField.addTargetForControlEvent(
            RHTextField.controlEvent.TextChange,
            () => {
                if (this.tableView.isMemberOfViewTree) {
                    this.updateTableDataByFiltering()
                }
            }
        )
        
        // 4. Header Replacement
        // @ts-ignore
        this.tableHeaderView.removeFromSuperview()
        this.tableHeaderView = new RHTableHeaderView(this.elementID + "Header").configuredWithObject({
            orderingStateDidChange: this.sortTableData.bind(this)
        })
        this.addSubview(this.tableHeaderView)
        
        // 5. Row Factory Override
        this.tableView.newReusableViewForIdentifier = (_identifier, rowIDIndex) => {
            const rowView = new RHTableRowView(this.elementID + "TableRow" + rowIDIndex)
            
            rowView.configureWithObject({
                expandedValueDidChange: async (_row: UIView, event: MouseEvent) => {
                    this.rowExpandedValueDidChange(rowView, event)
                    this.keepRowStaticDuringNextUpdate(rowView)
                    await this.updateTableDataByFiltering()
                    this.setNeedsLayoutUpToRootView()
                }
            })
            
            return rowView
        }
    }
    
    
    override layoutSubviews() {
        
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        
        const bounds = this.bounds.rectangleWithInsets(padding, padding + 2, padding, padding)
        
        this.titleLabel.frame = bounds.rectangleWithHeight(labelHeight)
        // Hide the title label if there is no text
        this.titleLabel.hidden = !this.titleLabel.text
        
        const searchTextFieldTopY = IF(!this.titleLabel.hidden)(() =>
            padding * 0.5 + this.titleLabel.frame.bottomLeft.y
        ).ELSE(() =>
            padding
        )
        this.searchTextField.frame = new UIRectangle(padding, searchTextFieldTopY, padding * 2.5, bounds.width)
        this.searchTextField.hidden = !this.isSearchAvailable
        
        const previousFrameFromTableHeader: UIRectangle = IF(this.isSearchAvailable)(() =>
            this.searchTextField.frame
        ).ELSE_IF(this.titleLabel.text)(() =>
            this.titleLabel.frame
        ).ELSE(() =>
            this.bounds.rectangleWithHeight(0)
        )
        const tableHeaderTopPadding = IF(!this.searchTextField.hidden || !this.titleLabel.hidden)(() =>
            padding
        ).ELSE(() =>
            0
        )
        
        this.tableHeaderView.frame = previousFrameFromTableHeader.rectangleForNextRow(
            tableHeaderTopPadding,
            padding * 3
        ).rectangleWithInsets(-padding + 1, -padding + 1, 0, 0)
        this.tableView.frame = new UIRectangle(
            this.bounds.x + 1,
            this.tableHeaderView.frame.bottomLeft.y,
            bounds.height - this.tableHeaderView.frame.bottomLeft.y,
            this.bounds.width - 2 - 2
        )
        
        
        if (this.tableHeaderView.boundsHaveChangedSinceLayout && this.hasResizableColumns) {
            
            const minWidths = this.minWidths
            const weights = this.tableHeaderView.cellWeights.map(
                (weight, index) => [this.userRequestedWeights?.[index] ?? weight, weight].min()
            )
            
            const weightDescriptors: {
                currentWeight: number;
                index: number;
                widthWithCurrentWeight: number;
                minWidth: number,
                widthLeftAboveMin: number
                needsExpanding: boolean,
            }[] = []
            
            const availableTotalWidth = bounds.width - this.tableHeaderView.sideCellsWidth * 2
            
            for (let i = 1; i < this.tableHeaderView.cells.length - 1; i++) {
                
                const weight = weights[i]
                const minWidth = minWidths[i]
                
                const widthWithCurrentWeight = (weight / weights.summedValue) * availableTotalWidth
                
                const needsExpanding = widthWithCurrentWeight < minWidth
                
                weightDescriptors.push({
                    
                    index: i,
                    currentWeight: weight,
                    minWidth: minWidth,
                    widthWithCurrentWeight: widthWithCurrentWeight,
                    widthLeftAboveMin: widthWithCurrentWeight - minWidth,
                    needsExpanding: needsExpanding
                    
                })
                
                
            }
            
            const sortedWeightDescriptors = weightDescriptors.copy().sort((
                a,
                b
            ) => (a.widthLeftAboveMin) - (b.widthLeftAboveMin))
            
            const weightDescriptorsThatNeedExpanding = sortedWeightDescriptors.filter(
                descriptor => descriptor.needsExpanding
            )
            
            const weightDescriptorsThatHaveSpace = sortedWeightDescriptors.filter(
                descriptor => !descriptor.needsExpanding
            )
            
            const totalSpace = weightDescriptorsThatHaveSpace.everyElement.widthLeftAboveMin.UI_elementValues?.summedValue ?? 0
            
            const totalSpaceNeededForExpansion = weightDescriptorsThatNeedExpanding.map(
                descriptor => -descriptor.widthLeftAboveMin).summedValue
            
            weightDescriptorsThatNeedExpanding.forEach(descriptor => weights[descriptor.index] = descriptor.minWidth)
            weightDescriptorsThatHaveSpace.forEach(descriptor => {
                
                const spaceChangeMultiplier = descriptor.widthLeftAboveMin / totalSpace
                
                weights[descriptor.index] = descriptor.widthWithCurrentWeight - totalSpaceNeededForExpansion * spaceChangeMultiplier
                
            })
            
            
            this.tableHeaderView.cellWeights = weights
            this.tableView.cellWeights = weights
            
            this.tableView.setNeedsLayout()
            
            
        }
        
        
    }
    
    
    override intrinsicContentHeight = (constrainingWidth: number = 0): number => {
        
        const padding = this.core.paddingLength
        const labelHeight = padding
        
        var result = padding * 2 + padding * 2 + padding + 50 + //padding +
            this.tableView.intrinsicContentHeight(constrainingWidth) + padding * 1.5 //* 2//.5
        
        return result
        
    }
    
    
}
